import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { Toolbar, TextField as MuiTextField, Typography, Paper, Box, Tabs, Tab, Tooltip, Badge } from '@mui/material';
import { AddItemButton, Button, Datagrid, EditButton, FunctionField, Link, List, ListContextProvider, Loading, ResourceContextProvider, ShowButton, SimpleShowLayout, TextField, TextInput, Title, TopToolbar, useList } from 'react-admin';
import { useParams } from 'react-router-dom';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import PrintIcon from '@mui/icons-material/Print';
import PersonIcon from '@mui/icons-material/Person';
import TvIcon from '@mui/icons-material/Tv';
import SensorsIcon from '@mui/icons-material/Sensors';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleIcon from '@mui/icons-material/Article';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import LayersIcon from '@mui/icons-material/Layers';

const fetchDrawings = async () => {
    const res = await fetch('https://technical-drawings.fortesinteractive.com/api/drawing');
    const json = await res.json();
    return {
        data: json,
        total: json.length
    };
};

const fetchDrawingSpecific = async (id) => {
    const res = await fetch('https://technical-drawings.fortesinteractive.com/api/drawing/' + id);
    const json = await res.json();
    return {
        data: json,
        total: json.length
    };
};
const fetchDrawingDetails = async (id, revision) => {
    const res = await fetch('https://technical-drawings.fortesinteractive.com/api/drawing/' + id + "/" + revision);
    const json = await res.json();
    return json
};

const TechDrawings = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("");
    const [knownRevisions, setKnownRevisions] = useState({});
    const [missingRevisions, setMissingRevisions] = useState([]);

    const listContext = useList({ 
        data,
        isLoading: loading,
        isFetching: loading,
        resource: "tech-drawings",
        /*filterCallback: (r) => {console.log(filter); return (r.drawing || "").includes(filter); }*/
    });

    useEffect(() => {
        setLoading(true);
        fetchDrawings()
            .then(({ data, total }) => {
                const d = data.map(x => { return { drawing: x, id: x }; });
                setData(d);
                setFilteredData(d);
                setTotal(total);
                setLoading(false);
                setMissingRevisions(data);
            }).catch((err) => {
                console.error(err);
                setError(err);
            });
    }, []);

    useEffect(() => {
        if (missingRevisions.length) {
            const pop = missingRevisions.shift();
            if (!knownRevisions[pop]) {
                fetchDrawingSpecific(pop)
                    .then(({data, total}) => {
                        setMissingRevisions(missingRevisions.filter( x => x != pop));
                        knownRevisions[pop] = data;
                        setKnownRevisions(knownRevisions);
                    })
                    .catch((err) => {
                        console.error(err);
                        setMissingRevisions(missingRevisions.filter( x => x != pop));
                    });
            } else {
                setMissingRevisions(missingRevisions.filter( x => x != pop));
            }
        }
    }, [missingRevisions]);

    return <><Title title="Technical Drawings" />
        { loading && <Loading /> }
        { error && <Card><CardContent><h1>Something went wrong</h1>{error+""}</CardContent></Card> }
        { !loading && !error && <>
        <Toolbar style={{paddingLeft: 0, minHeight: '60px'}}>
            <div className="filter-field">
                <MuiTextField label="Drawing"
                onChange={(e) => { console.log("..", e.target.value); setFilteredData(data.filter((item) => {
                    return item.drawing.toLowerCase().includes( e.target.value.toLowerCase() );
                })); }} />
            </div>
        </Toolbar>
        <ResourceContextProvider value="tech-drawings"><ListContextProvider value={{...listContext, data: filteredData}}>
            <Datagrid bulkActionButtons={false} rowStyle={() => {return {"backgroundImage": "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))"} }}>
                <TextField source="drawing" label="Drawing" sortable={false} />
                <FunctionField render={record => knownRevisions[record.drawing] ? knownRevisions[record.drawing].length / 2 : ''} label="Revisions" />
                <FunctionField render={record => knownRevisions[record.drawing] ? niceDate(parseCustomDateTime(knownRevisions[record.drawing][knownRevisions[record.drawing].length - 1])) : ''} label="Last revision" />
                <ShowButton></ShowButton>
            </Datagrid>
        </ListContextProvider></ResourceContextProvider><br /><br /></> }
        </>
}; 

function parseCustomDateTime(dateTimeStr) {
    if (!dateTimeStr)
        return null;
    const correctedStr = dateTimeStr.replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
    const dateObj = new Date(correctedStr);
    if (isNaN(dateObj.getTime())) {
      throw new Error('Invalid date format');
    }  
    return dateObj;
}
function niceDate(d) {
    if (!d)
        return null;
    return d.toLocaleDateString() + " - " + d.toLocaleTimeString()
}

function removeDuplicates(arr) {
    const result = [];
    for (let i = 0; i < arr.length; i++) {
      if (!result.includes(arr[i])) {
        result.push(arr[i]);
      }
    }
    return result;
}

function hr(str) {
    if (!str) return "";
    const withSpaces = str.replace(/([a-z])([A-Z][a-z])/g, '$1 $2') // e.g., "someHTTPValue" -> "some HTTPValue"
                         .replace(/([a-z])([A-Z])/g, '$1 $2');     // fallback for non-acronym transitions
    const r = withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    if (r.length < 3)
        return r.toUpperCase();
    else return r;
}

function ico(key) {
    switch (key) {
        case 'printing':
            return <PrintIcon />
        case 'sensor':
            return <SensorsIcon />
        case 'display':
            return <TvIcon />
        case 'customer':
            return <PersonIcon />
        case 'glass':
            return <AspectRatioIcon />
        case 'customization':
            return <AutoFixHighIcon />
        case 'product':
            return <InfoOutlinedIcon />
        case 'status':
            return <ArticleIcon />
        case 'layers':
            return <LayersIcon />
        default:
            return <SettingsIcon />
    }
}

function TooltipTab(props) {
    const { tooltip, label, count } = props;
  
    return (
      <Tooltip title={tooltip}>
        <Tab
          component="a"
          onClick={(event) => {
            event.preventDefault();
          }}
          {...props}
          label={count? <Badge badgeContent={count} color="warning">{label}</Badge> : label}
        />
      </Tooltip>
    );
}
function TabPanel(props) {
    const { children, value, index, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  function RenderTable(props) {
    const data = props.data;
    const dataPrev = props.dataPrev;
    const hasPrev = props.hasPrev;
    const colSpan = props.colSpan || 100;
    const isRoot = !props.colSpan;
    let cls = 'value';    
    if (typeof data !== 'object' || data === null) {
        let tt = null;
        if (hasPrev) {
            if (data !== dataPrev) {
                cls += ' change';
                let strOld = String(dataPrev);
                if (strOld.length > 250) {
                    strOld = strOld.substring(0, 250) + '...';
                }
                tt = <><b>Previous value:</b> <span style={{color:"#F88"}}>{strOld}</span><br /></>;
            }
        }
        const strData = String(data);
        let str = strData;
        
        let tooLong = false;
        if (str.length > 250) {
            tooLong = true;
            str = str.substring(0, 250) + '...';
            cls += ' toolong';
        }
        tt = <>{tt}<b>Current value:</b> <span style={{color:"#AAF"}}>{str}</span></>;
        if (((data || "") + "").startsWith("data:image")) {
            cls = cls.replace(' toolong', '');
            return { 
                rows: 1, cols: 1, key: null, isLastCell: true,
                content: [<Tooltip placement='top' title={<table><tbody>
                    <tr><td><b>Previous image:</b></td><td> {dataPrev? <img src={dataPrev} className="img-in-rev-tooltip old" /> : <span style={{color:"#F88"}}>None</span>}</td></tr>
                    <tr><td><b>Current image:</b></td><td><img src={data} className="img-in-rev-tooltip new" /></td></tr>
                </tbody></table>}><span className={cls}><img src={data} /></span></Tooltip>]};
        }
        if (data === null)
            return { 
                rows: 1, cols: 1, key: null, isLastCell: true,
                content: [<Tooltip placement='top' title={tt}><span className={cls}>&#8203;</span></Tooltip>] };
        if (data === true)
            return { 
                rows: 1, cols: 1, key: null, isLastCell: true,
                content: [<Tooltip placement='top' title={tt}><span className={cls}><CheckBoxIcon className='val-ico' /></span></Tooltip>] };
        if (data === false)
            return { 
                rows: 1, cols: 1, key: null, isLastCell: true,
                content: [<Tooltip placement='top' title={tt}><span className={cls}><CheckBoxOutlineBlankIcon className='val-ico' /></span></Tooltip>] };
        return { 
            rows: 1, cols: 1, key: null, isLastCell: true,
            content: [<Tooltip placement='top' title={tt}><span className={cls} onClick={ tooLong? (ev)=>{ 
            ev.currentTarget.textContent = strData; ev.currentTarget.classList.remove('toolong') } : undefined }>{str || (<>&#8203;</>)}</span></Tooltip>] };
      }
    
      if (Array.isArray(data)) {
        let rowNum = 0;
        let subs = [];
        for (let index = 0; index < data.length; index++) {
            const item = data[index];
            const sub = RenderTable({data: item, dataPrev: dataPrev?.[index], hasPrev: hasPrev, colSpan: colSpan - 1});
            rowNum += sub.rows;
            //subs.push(sub);            
            subs = subs.concat(sub.content.map(s => {return { ...sub, content: s, key: index }}));
        }

        let cells = [];
        let lastKey = null;
        let arrIndex = 0;
        for (let index = 0; index < subs.length; index++) {
            const sub = subs[index];
            const cs = (sub.isLastCell ? colSpan : 1);
            cells.push(<React.Fragment key={index}>
              { lastKey != sub.key && <td rowSpan={sub.rows} className="att-title array">#{arrIndex + 1}:</td> }
              { sub.isLastCell && <td rowSpan={sub.rows} colSpan={cs}>{sub.content}</td> }
                { !sub.isLastCell && sub.content }
            </React.Fragment>)
            if (lastKey != sub.key) {
                arrIndex++;
                lastKey = sub.key;
            }
        }

        const ret = { rows: rowNum, cols: 2, content: cells}
        return ret;
      }
    
      const entries = Object.entries(data);
      let rowNum = 0;
      let colNum = 0;
      let subs = [];
      entries.map(([key, value]) => {
        const sub = RenderTable({data: value, dataPrev: dataPrev?.[key], hasPrev: hasPrev, colSpan: colSpan - 1});
        rowNum += sub.rows;
        if (sub.cols > colNum)
            colNum = sub.cols;
        sub.key = key;
        //subs.push(sub);
        subs = subs.concat(sub.content.map(s => {return { ...sub, content: s }}));
      });

      let cells = [];
      let lastKey = null;
      for (let index = 0; index < subs.length; index++) {
        const sub = subs[index];
        const cs = (sub.isLastCell ? colSpan : 1);
        if (isRoot) 
            cells.push(<tr key={index}>
                { lastKey != sub.key && <td rowSpan={sub.rows} className="att-title">{hr(sub.key)}:</td> }
                { sub.isLastCell && <td rowSpan={sub.rows} colSpan={cs}>{sub.content}</td> }
                { !sub.isLastCell && sub.content }
            </tr>);
        else
            cells.push(<React.Fragment key={index}>
                { lastKey != sub.key && <td rowSpan={sub.rows}  className="att-title">{hr(sub.key)}:</td> }
                { sub.isLastCell && <td rowSpan={sub.rows} colSpan={cs}>{sub.content}</td> }
                { !sub.isLastCell && sub.content }
            </React.Fragment>);
        lastKey = sub.key;
      }

      const ret = { rows: rowNum, cols: 2, content: cells}
      return ret;
}

function countChanges(data, dataPrev) {
    if (typeof data !== 'object' || data === null) {
        if (data !== dataPrev)
            return 1;
        else 
            return 0;
    }

    let cnt = 0;
    if (Array.isArray(data)) {
    
    for (let i = 0; i < data.length; i++) {
        cnt += countChanges(data[i], dataPrev?.[i]);
    }
    return cnt;
    }

    const keys = Object.keys(data);
    for (let i in keys) {
        cnt += countChanges(data[keys[i]], dataPrev?.[keys[i]]);
    }
    return cnt;
      
}

function renderRevisionDetails(details, selectedTab, setSelectedTab)
{
    const tabs = [];
    const panels = [];
    for (const key in details.data) {
        const table = RenderTable({data: details.data[key], dataPrev:details.dataPrev?.[key] || undefined, hasPrev: !!(details.dataPrev?.[key])})
        tabs.push(<TooltipTab count={ !!details.dataPrev ? countChanges(details.data[key], details.dataPrev?.[key]) : 0} tooltip={hr(key)} value={key} key={key} label={ico(key)} />);
        panels.push(<TabPanel value={key} key={key} index={selectedTab}>
            <Typography variant="h6">{hr(key)}</Typography><br />
            <table className="rev-detail">
                <tbody>
                    {table.content}
                </tbody>
            </table>
        </TabPanel>);
    }

    if (!details.data[selectedTab]) {
        setSelectedTab('product');
    }

    return <Card><Tabs variant="fullWidth" value={details.data[selectedTab] ? selectedTab : 'product'} onChange={(_, v) => { setSelectedTab(v); }}>
        { tabs }
    </Tabs>{panels}</Card>
}

const TechDrawingSpecific = () => {
    const { id } = useParams();

    const [data, setData] = useState([]);
    const [selected, setSelected] = useState(null);
    const [details, setDetails] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selectedTab, setSelectedTab] = useState("product");

    const listContext = useList({ data, isLoading: loading, isFetching: loading, resource: "tech-drawings" });

    useEffect(() => {
        setLoading(true);
        fetchDrawingSpecific(id)
            .then(({ data, total }) => {
                let newData = removeDuplicates(data).map(x => { return { prevRevision: null, revision: x, id: x }; });
                let prevRevision = null;
                newData = newData.map(d => { d.prevRevision = prevRevision; prevRevision = d.revision; return d; })
                setData(newData);
                setTotal(total);
                setLoading(false);
            }).catch((err) => {
                console.error(err);
                setError(err);
            });
    }, []);

    const showRevisionDetails = (id, record) => {
        setSelected(record);
        setDetails(null);
        fetchDrawingDetails(id, record.revision)
            .then((data) => {
                if (record.prevRevision) {
                    fetchDrawingDetails(id, record.prevRevision)
                        .then((dataPrev) => {
                            setDetails({data, dataPrev});
                        })
                        .catch((err) => {
                            console.error(err);
                            setError(err);
                        });
                } else {
                    setDetails({data: data, dataPrev: null});
                }
            })
            .catch((err) => {
                console.error(err);
                setError(err);
            });
    }

    return <><Title title={"Technical Drawing: " + id} />
        { loading && <Loading /> }
        { error && <Card><CardContent><h1>Something went wrong</h1>{error+""}</CardContent></Card> }
        { !loading && !error && <ResourceContextProvider value="tech-drawings"><ListContextProvider value={listContext}>
        <Box display="flex">
            {/* Main Content */}
            <Box flex={1} p={2}>
                <Datagrid className="revision-left-table" bulkActionButtons={false} rowStyle={() => {return {"backgroundImage": "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))"} }}>
                    <FunctionField label="Revision" render={record => <span>{ niceDate(parseCustomDateTime(record?.revision)) }</span>} />                    
                    <FunctionField render={record => <Link target='blank' to={`https://technical-drawings.fortesinteractive.com/admin-form/${id}/${record?.revision}`}><Button startIcon={<EditIcon />} label='Edit'></Button></Link>}></FunctionField>
                    <FunctionField render={record => <Link target='blank' to={`https://technical-drawings.fortesinteractive.com/drawings/${id}/${record?.revision}.pdf`}><Button startIcon={<InsertDriveFileIcon />} label='PDF'></Button></Link>}></FunctionField>
                    <FunctionField render={record => <Button startIcon={<VisibilityIcon />} label='Detail ⯈' onClick={() => { showRevisionDetails(id, record); }}></Button> } />
                    <FunctionField render={record => record?.revision == selected?.revision ? <><span className="selected-revision-row"></span><span className="selected-revision-row-2"></span></>: null} />
                </Datagrid>
            </Box>
            <Box flex={1} p={2}>
                <div style={{ margin: "1em" }}>
                    { selected && selected.revision && <div style={{float: 'right'}}>
                        <Link target='blank' to={`https://technical-drawings.fortesinteractive.com/admin-form/${id}/${selected.revision}`}><Button startIcon={<EditIcon />} label='Edit'></Button></Link>
                        &nbsp;<Link target='blank' to={`https://technical-drawings.fortesinteractive.com/drawings/${id}/${selected.revision}.pdf`}><Button startIcon={<InsertDriveFileIcon />} label='PDF'></Button></Link>
                    </div>}
                    <Typography variant="h4">Revision details</Typography><br />
                    {!selected && <Typography variant="body1">Start by selecting a revision.</Typography>}
                    {selected && selected.revision && <>
                        <Typography variant="h6">Name: {id}</Typography>
                        <Typography variant="h6">Date: {niceDate(parseCustomDateTime(selected.revision))}</Typography>
                        <br />
                        {!details && <span>Loading...</span>}
                        {!!details && renderRevisionDetails(details, selectedTab, setSelectedTab)}
                    </>}
                </div>
            </Box>
        </Box>
        </ListContextProvider></ResourceContextProvider> }
    </>;
}

export default TechDrawings;
export { TechDrawingSpecific };